/**
 * Чистые функции показа переписки: время, вид пузыря, подпись последней реплики, разбивка по дням.
 * Ничего не знают про React и сеть — проверяются отдельно.
 */
import {
  MESSAGE_ACTIVITY, MESSAGE_INCOMING, type CwAttachment, type CwMessage, type InboxConversation, type UiMessage,
} from '@/api/inboxTypes';

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

const pad = (n: number) => String(n).padStart(2, '0');

/** Начало местных суток. */
function dayStart(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Сколько календарных дней назад (по местному времени). */
export function daysAgo(ts: number, now: Date): number {
  return Math.round((dayStart(now) - dayStart(new Date(ts * 1000))) / 86_400_000);
}

export function formatClock(ts: number): string {
  const d = new Date(ts * 1000);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Время в строке списка: сегодня — часы, вчера — «Вчера», неделя — день недели, дальше — дата. */
export function formatRowTime(ts: number, now: Date = new Date()): string {
  if (!ts) return '';
  const ago = daysAgo(ts, now);
  const d = new Date(ts * 1000);
  if (ago <= 0) return formatClock(ts);
  if (ago === 1) return 'Вчера';
  if (ago < 7) return WEEKDAYS[d.getDay()];
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear ? `${d.getDate()} ${MONTHS[d.getMonth()]}` : `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear()).slice(2)}`;
}

/** Подпись разделителя дня в переписке. */
export function dayLabel(ts: number, now: Date = new Date()): string {
  const ago = daysAgo(ts, now);
  if (ago <= 0) return 'Сегодня';
  if (ago === 1) return 'Вчера';
  const d = new Date(ts * 1000);
  const base = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base} ${d.getFullYear()}`;
}

// ── Вид пузыря ──────────────────────────────────────────────────────────────

export type BubbleKind = 'them' | 'me' | 'note' | 'bot' | 'sys';

/**
 * Какой пузырь рисовать.
 *  • служебное (активность Chatwoot) — серая строка по центру;
 *  • заметка (private) — жёлтый, клиент не видит;
 *  • входящее — «они»;
 *  • исходящее от бота — пунктирный «бот»;
 *  • остальное исходящее — «я» (в том числе от коллеги — подпись имени ставит вызывающий).
 */
export function bubbleKind(m: CwMessage): BubbleKind {
  if (m.message_type === MESSAGE_ACTIVITY) return 'sys';
  if (m.private) {
    // Служебная заметка бота «по вызову» (предложение, итог) — пунктирный пузырь бота, а не жёлтая заметка человека.
    return m.sender?.type === 'agent_bot' && m.content_attributes?.unia_bot_invocation_id ? 'bot' : 'note';
  }
  if (m.message_type === MESSAGE_INCOMING) return 'them';
  if (m.sender?.type === 'agent_bot') return 'bot';
  return 'me';
}

/** Исходящее от коллеги (не меня): над пузырём подписываем имя. */
export function colleagueName(m: CwMessage, myUserId: number | null): string | null {
  if (m.message_type === MESSAGE_INCOMING || m.message_type === MESSAGE_ACTIVITY) return null;
  if (m.sender?.type !== 'user') return null;
  if (myUserId !== null && m.sender.id === myUserId) return null;
  return m.sender.name || null;
}

export function hasVoice(m: CwMessage): boolean {
  return (m.attachments ?? []).some((a) => a.file_type === 'audio');
}

// ── Подпись последней реплики в списке ─────────────────────────────────────

export interface Preview {
  /** «Вы: », «Бот: », «Заметка: » или пусто. */
  prefix: string;
  text: string;
  voice: boolean;
}

function attachmentWord(a: CwAttachment): string {
  switch (a.file_type) {
    case 'image': return 'Фото';
    case 'video': return 'Видео';
    case 'audio': return 'Голосовое';
    case 'location': return 'Геопозиция';
    case 'contact': return 'Контакт';
    default: return 'Файл';
  }
}

/**
 * Длительность голосового API не отдаёт (в push_event_data вложения её нет), поэтому
 * «Голосовое 0:18» из прототипа показать нечем — пишем «Голосовое».
 */
export function previewOf(c: InboxConversation, myUserId: number | null): Preview {
  const m = c.lastMessage;
  if (!m) return { prefix: '', text: 'Сообщений пока нет', voice: false };
  let prefix = '';
  if (m.private) prefix = 'Заметка: ';
  else if (m.message_type !== MESSAGE_INCOMING) {
    prefix = m.sender?.type === 'agent_bot' ? 'Бот: '
      : m.sender?.type === 'user' && myUserId !== null && m.sender.id !== myUserId ? `${(m.sender.name || 'Коллега').split(' ')[0]}: `
        : 'Вы: ';
  }
  const att = m.attachments?.[0];
  const text = (m.content ?? '').trim();
  if (att?.file_type === 'audio') return { prefix, text: text || 'Голосовое', voice: true };
  if (text) return { prefix, text, voice: false };
  if (att) return { prefix, text: attachmentWord(att), voice: false };
  return { prefix, text: 'Сообщение', voice: false };
}

/** Кто ведёт переписку — подпись под последней репликой. null — моя или никто (не подписываем). */
export function whoLine(c: InboxConversation, myUserId: number | null): { text: string; bot: boolean } | null {
  if (c.assigneeKind === 'AgentBot') return { text: 'Ведёт бот', bot: true };
  if (!c.assignee) return { text: 'Без ответственного', bot: false };
  if (c.assignee.id === myUserId) return null;
  return { text: `Отвечает ${c.assignee.name || 'коллега'}`, bot: false };
}

/** Цвет аватара — стабильный по номеру контакта. */
export const AVATAR_COLORS = ['#0E7490', '#7C3AED', '#B45309', '#BE123C', '#15803D', '#1D4ED8', '#A21CAF', '#0F766E'];

export function avatarColor(seed: number): string {
  return AVATAR_COLORS[Math.abs(seed) % AVATAR_COLORS.length];
}

/** Две буквы для аватара. */
export function initials(name: string): string {
  const parts = name.replace(/[^\p{L}\p{N}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const letters = parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[1][0];
  return letters.toUpperCase();
}

// ── Лента: разделители дней ────────────────────────────────────────────────

export type TimelineItem =
  | { type: 'day'; key: string; label: string }
  | { type: 'msg'; key: string; message: UiMessage; kind: BubbleKind };

/** Сообщения (по возрастанию) → элементы ленты с разделителями дней. */
export function buildTimeline(messages: UiMessage[], now: Date = new Date()): TimelineItem[] {
  const out: TimelineItem[] = [];
  let lastDay = -Infinity;
  for (const m of messages) {
    const day = dayStart(new Date(m.created_at * 1000));
    if (day !== lastDay) {
      out.push({ type: 'day', key: `day-${day}`, label: dayLabel(m.created_at, now) });
      lastDay = day;
    }
    out.push({ type: 'msg', key: m.echo_id && m.local ? `local-${m.echo_id}` : `m-${m.id}`, message: m, kind: bubbleKind(m) });
  }
  return out;
}

export function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  return `${Math.floor(sec / 60)}:${pad(Math.floor(sec % 60))}`;
}

export function formatBytes(n: number | undefined): string {
  if (!n) return '';
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} КБ`;
  return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`;
}
